import { action, computed, observable, values, makeObservable } from 'mobx';

import { CallProviderType } from '@gem/call/Model/callModel.types';

import Entities from '../index';
import { IncomingCall } from './IncomingCall/IncomingCall';

interface IIncomingCallsAddParams {
  groupId?: string;
  channelId: string;
  callerId: string;
  callVisibleDuration: number;
  callProvider: CallProviderType;
}

export class IncomingCalls {
  @observable data = observable.map<string, IncomingCall>({}, { deep: false });
  root: typeof Entities;

  constructor(root: typeof Entities) {
    makeObservable(this);
    this.root = root;
  }

  @action reset(): void {
    this.data.forEach((call: IncomingCall) => {
      call.dispose();
    });
    this.data.clear();
  }

  @action
  add({
    groupId,
    channelId,
    callerId,
    callVisibleDuration,
    callProvider,
  }: IIncomingCallsAddParams): void {
    const incomingCall = new IncomingCall({
      groupId,
      channelId,
      initiatorId: callerId,
      callVisibleDuration,
      callProvider,
    });

    this.data.set(channelId, incomingCall);
  }

  @action
  delete(channelId?: string | null, groupId?: string | null): void {
    let call = null;
    if (groupId) {
      call = this.getCallByGroupId(groupId);
    }
    if (!call && channelId) {
      call = this.getCallByChannelId(channelId);
    }

    if (call && call.channelId === channelId) {
      call.dispose();
      this.data.delete(call.channelId);
    }
  }

  @computed
  get calls(): readonly IncomingCall[] {
    return values(this.data);
  }

  @computed
  get hasCalls(): boolean {
    return !!this.calls.length;
  }

  getCallByGroupId(groupId: string): IncomingCall | null {
    return this.calls.find((call) => call.groupId === groupId) || null;
  }

  getCallByChannelId(channelId: string): IncomingCall | null {
    return this.data.get(channelId) || null;
  }
}
